import { type RefObject } from "react";
import { SortDirection } from "~/features/search/models/models";

/**
 * Splits the sort codes into two parts - the actual code and the sorting direction.
 * Sorting direction is null in case the sort option doesn't support ASC/DESC directions
 * For example - Relevance
 * @param {string} code - sort code
 * @returns {{code: string; sortingDirection: SortDirection | null}}
 */
export function splitSortingCode(code: string): {
  code: string;
  sortingDirection: SortDirection | null;
} {
  const parts = code.split("-");
  let last = parts.pop();
  last = last ?? "";

  let direction = null;
  if (last === "asc") {
    direction = SortDirection.ASC;
  } else if (last === "desc") {
    direction = SortDirection.DESC;
  }

  if (direction === null) {
    parts.push(last);
  }

  const finalCode = parts.join("-");

  return {
    code: finalCode,
    sortingDirection: direction,
  };
}

type key = string | number;

export function immutableRemoveKeys<T extends object>(obj: T, ...keys: key[]): T {
  const newObj = Object.assign({}, obj);
  for (const keyToRemove of keys) {
    delete (newObj as Record<string | number, unknown>)[keyToRemove];
  }
  return newObj;
}

export function scrollToTop() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.setTimeout(() => (window as any).$(window).scrollTop(0), 0);
}

export function scrollToElement(ref: RefObject<HTMLElement>) {
  ref.current?.scrollIntoView({ behavior: "smooth" });
}

export function uniqueValues<T extends string | number>(data: T[]): string[] {
  const map = data.reduce(
    (acc, item) => {
      acc[item] = true;
      return acc;
    },
    {} as { [key: string]: boolean },
  );
  return Object.keys(map);
}
export function uniqueObjects<T extends object>(data: T[], keyFun: (a: T) => string | number): T[] {
  const keyCheck: Dictionary<boolean> = {};

  return data.filter((item) => {
    const key = keyFun(item);
    if (!keyCheck[key]) {
      keyCheck[key] = true;
      return true;
    }
    return false;
  });
}

export function variantClassname(baseCls: string, variant?: string) {
  return variant ? `${baseCls} ${baseCls}--${variant}` : baseCls;
}

export const resetQualtrics = () => {
  if ("QSI" in window) {
    // @ts-ignore
    const { unload, load, run } = window.QSI?.API ?? {};
    unload?.();
    load?.();
    run?.();
  }
};

const EMPTY_LIST: never[] = [];

export function getEmptyList<T>(): T[] {
  return EMPTY_LIST;
}
